/**
 * Starter scenario templates (spec §15). Each takes the current plan and
 * returns a modified copy — a starting point the user then edits.
 */
import type { Plan } from '../model/types';
import { CURRENT_YEAR } from '../model/defaults';

export interface ScenarioTemplate {
  key: string;
  label: string;
  apply: (base: Plan) => Plan;
}

export const SCENARIO_TEMPLATES: ScenarioTemplate[] = [
  {
    key: 'retire_earlier',
    label: 'Retire 2 years earlier',
    apply: (base) => {
      const p: Plan = structuredClone(base);
      for (const person of p.household.people) {
        const oldAge = person.retirementAge;
        person.retirementAge = Math.max(45, oldAge - 2);
        // Work income that ended at retirement follows it down.
        for (const inc of p.incomes) {
          if (inc.ownerId === person.id && (inc.type === 'w2' || inc.type === 'self_employment') &&
              inc.endAge >= oldAge) {
            inc.endAge = person.retirementAge;
          }
        }
      }
      return p;
    },
  },
  {
    key: 'delay_ss_70',
    label: 'Delay Social Security to 70',
    apply: (base) => {
      const p: Plan = structuredClone(base);
      for (const person of p.household.people) person.socialSecurity.claimingAge = 70;
      p.decisions.claimingAgeOverride = undefined;
      return p;
    },
  },
  {
    key: 'relocate',
    label: 'Relocate to a no-tax state',
    apply: (base) => {
      const p: Plan = structuredClone(base);
      const retirementYear = Math.min(
        ...p.household.people.map((x) => x.birthYear + x.retirementAge),
      );
      p.household.relocations = [
        { year: Math.max(CURRENT_YEAR + 1, retirementYear), toState: 'FL' },
      ];
      return p;
    },
  },
  {
    key: 'downsize',
    label: 'Downsize the house',
    apply: (base) => {
      const p: Plan = structuredClone(base);
      const retirementYear = Math.min(
        ...p.household.people.map((x) => x.birthYear + x.retirementAge),
      );
      const saleYear = Math.max(CURRENT_YEAR + 1, retirementYear);
      const home = p.properties.find((x) => x.type === 'primary');
      if (home) {
        home.plannedSaleYear = saleYear;
        p.properties.push({
          id: `prop_downsize_${p.properties.length + 1}`,
          label: 'Smaller home',
          type: 'primary',
          ownership: 'own_outright',
          currentValue: home.currentValue * 0.6,
          costBasis: home.currentValue * 0.6,
          purchaseYear: saleYear,
          sellingCostPct: home.sellingCostPct,
          propertyTaxAnnual: home.propertyTaxAnnual * 0.6,
          insuranceAnnual: home.insuranceAnnual * 0.6,
          hoaAnnual: home.hoaAnnual,
          maintenancePctOfValue: home.maintenancePctOfValue,
        });
      }
      return p;
    },
  },
  {
    key: 'crash',
    label: 'Bad markets (pessimistic view)',
    apply: (base) => {
      const p: Plan = structuredClone(base);
      p.activeAssumptionLabel = 'pessimistic';
      return p;
    },
  },
  {
    key: 'ltc',
    label: 'Long-term care event',
    apply: (base) => {
      const p: Plan = structuredClone(base);
      const person = p.household.people[0]!;
      p.ltcEvents ??= [];
      const existing = p.ltcEvents.find((e) => e.personId === person.id);
      if (existing) {
        existing.enabled = true;
      } else {
        p.ltcEvents.push({
          personId: person.id,
          enabled: true,
          onsetAge: 85,
          durationYears: 3,
          annualCostToday: 110_000,
          careType: 'assisted_living',
        });
      }
      return p;
    },
  },
];
